import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { SEARCH_TOKENS } from '../graphql/queries/tokens';
import { useTheme } from './ThemeProvider';
import { usePositionContext } from '../contexts/PositionContext';
import { formatCurrency } from '../utils/formatters';
import { tokenRoutePath } from '../utils/tokenRoutes';

const Jump = ({ onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredResults, setFilteredResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { positions, loading: positionsLoading } = usePositionContext();
  
  const [searchTokens, { loading: searchLoading }] = useLazyQuery(SEARCH_TOKENS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'ignore' // Don't show errors in UI for search
  });
  
  useEffect(() => {
    // Add event listener for keyboard shortcut (J key). Use capture so we get Escape
    // before modals when both Jump and a modal are open (first Esc closes Jump only).
    const handleKeyDown = (e) => {
      if (e.key === 'j' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen(true);
      } else if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        e.stopImmediatePropagation();
        closeSearch();
      }
    };
    
    // Allow external components to open the search
    const handleOpenSearch = () => setIsOpen(true);
    
    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('open-search', handleOpenSearch);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('open-search', handleOpenSearch);
    };
  }, [isOpen]);
  
  // Focus the input when overlay opens
  useEffect(() => {
    if (isOpen && searchRef.current) {
      setTimeout(() => {
        searchRef.current.focus();
      }, 10);
    }
  }, [isOpen]);

  // Helper function to create result items
  const createResultItem = useCallback((token, position = null, isFromSearch = false) => {
    const holdingsValue = position?.holdingsValue || null;
    const roi = position?.current?.roi || null;
    
    return {
      id: token.id || `search-${token.coingeckoApiId}`,
      name: token.name,
      ticker: token.ticker,
      coingeckoSlug: token.coingeckoSlug || null,
      coingeckoApiId: token.coingeckoApiId || null,
      marketCapRank: token.marketCapRank || null,
      holdingsValue,
      roi,
      hasPosition: !!position,
      isFromSearch,
      tokenId: token.id
    };
  }, []);

  // Sort positions by holdings value (highest first)
  const sortedPositions = useCallback(() => {
    if (!positions) return [];
    
    return [...positions]
      .filter(p => p.token) // Ensure token exists
      .sort((a, b) => {
        const aValue = parseFloat(a.holdingsValue?.replace(/[$,]/g, '') || '0');
        const bValue = parseFloat(b.holdingsValue?.replace(/[$,]/g, '') || '0');
        return bValue - aValue;
      })
      .map(position => createResultItem(position.token, position));
  }, [positions, createResultItem]);

  // Filter positions locally based on search term
  const filterPositions = useCallback((term) => {
    const sorted = sortedPositions();
    if (!term) return sorted;
    
    const lowerTerm = term.toLowerCase();
    return sorted.filter(item => 
      item.name.toLowerCase().includes(lowerTerm) || 
      item.ticker.toLowerCase().includes(lowerTerm)
    );
  }, [sortedPositions]);

  // Debounced API search
  const performApiSearch = useCallback(async (term) => {
    if (term.length <= 2) return [];
    
    try {
      const result = await searchTokens({ 
        variables: { query: term } 
      });
      
      if (result.data?.searchTokens) {
        return result.data.searchTokens.map(token => {
          // Map the API response fields to the expected token structure
          const mappedToken = {
            id: token.tokenId, // Map tokenId to id for createResultItem
            name: token.name,
            ticker: token.ticker,
            coingeckoApiId: token.coingeckoApiId,
            marketCapRank: token.marketCapRank,
            coingeckoSlug: token.coingeckoSlug || null
          };
          return createResultItem(mappedToken, null, true);
        });
      }
    } catch (error) {
      console.error('Search error:', error);
    }
    
    return [];
  }, [searchTokens, createResultItem]);

  // Main search effect
  useEffect(() => {
    const processSearch = async () => {
      setIsSearching(true);
      
      // Always filter local positions first
      const localResults = filterPositions(searchTerm);
      
      // If search term is short, only show local results
      if (searchTerm.length <= 2) {
        setFilteredResults(localResults);
        setSelectedIndex(0);
        setIsSearching(false);
        return;
      }
      
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Debounce API search for 1 second
      searchTimeoutRef.current = setTimeout(async () => {
        const apiResults = await performApiSearch(searchTerm);
        
        // Merge results: positions first, then new API results
        // Create sets for both tokenId and coingeckoApiId to ensure proper deduplication
        const existingTokenIds = new Set(localResults.map(r => r.tokenId).filter(Boolean));
        const existingCoingeckoApiIds = new Set(localResults.map(r => r.coingeckoApiId).filter(Boolean));
        
        // Debug logging
        console.log('Local results tokenIds:', Array.from(existingTokenIds));
        console.log('Local results coingeckoApiIds:', Array.from(existingCoingeckoApiIds));
        console.log('API results:', apiResults.map(r => ({ tokenId: r.tokenId, coingeckoApiId: r.coingeckoApiId, name: r.name })));
        
        const newApiResults = apiResults.filter(result => {
          // Filter out if tokenId matches (when both exist)
          if (result.tokenId && existingTokenIds.has(result.tokenId)) {
            console.log(`Filtering out ${result.name} by tokenId:`, result.tokenId);
            return false;
          }
          // Filter out if coingeckoApiId matches (fallback for tokens without tokenId)
          if (result.coingeckoApiId && existingCoingeckoApiIds.has(result.coingeckoApiId)) {
            console.log(`Filtering out ${result.name} by coingeckoApiId:`, result.coingeckoApiId);
            return false;
          }
          return true;
        });
        
        const mergedResults = [...localResults, ...newApiResults];
        setFilteredResults(mergedResults);
        setSelectedIndex(0);
        setIsSearching(false);
      }, 1000);
    };
    
    processSearch();
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, filterPositions, performApiSearch]);

  const closeSearch = () => {
    setIsOpen(false);
    setSearchTerm('');
    setSelectedIndex(0);
    setIsSearching(false);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filteredResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && filteredResults.length > 0) {
      const selectedResult = filteredResults[selectedIndex];
      handleSelectResult(selectedResult);
    }
  };
  
  const handleSelectResult = (result) => {
    const path = tokenRoutePath(result);
    if (path) {
      navigate(path);
    }
    closeSearch();
    onNavigate?.();
  };

  // Button styling based on theme
  const buttonClasses = darkMode
    ? "flex items-center justify-center p-2 rounded-full bg-gray-800 hover:bg-gray-700 shadow-lg"
    : "flex items-center justify-center p-2 rounded-full bg-white hover:bg-gray-100 shadow-lg border border-gray-200";
  
  // Icon color based on theme
  const iconColor = darkMode ? "text-yellow-400" : "text-blue-600";
  
  const searchDialog = isOpen ? createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[10000] flex items-start justify-center px-3 pt-14 sm:pt-20">
      <div
        className="w-full max-w-lg overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-800"
        style={{ maxHeight: 'calc(100vh - 5rem)' }}
      >
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 mr-2 ${darkMode ? 'text-yellow-400' : 'text-gray-400'}`} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                placeholder="Search tokens..."
                className="min-w-0 flex-1 bg-transparent border-none outline-none text-gray-700 dark:text-gray-200"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
              />
              {(isSearching || searchLoading) && (
                <div className="mr-2">
                  <svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              )}
              <button 
                onClick={closeSearch}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 10rem)' }}>
              {positionsLoading && filteredResults.length === 0 ? (
                <div className="p-4 text-center text-gray-500">Loading...</div>
              ) : filteredResults.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {searchTerm ? 'No tokens found' : 'Type to search for tokens'}
                </div>
              ) : (
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredResults.map((result, index) => (
                    <li 
                      key={result.id}
                      className={`p-3 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center ${index === selectedIndex ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
                      onClick={() => handleSelectResult(result)}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="truncate font-medium text-gray-800 dark:text-gray-200">
                            {result.name} ({result.ticker})
                          </span>
                        </div>
                        {result.hasPosition && (
                          <div className="truncate text-sm text-gray-500 dark:text-gray-400">
                            Holdings: {formatCurrency(result.holdingsValue)} · ROI: {result.roi}
                          </div>
                        )}
                        {result.isFromSearch && !result.hasPosition && result.marketCapRank && (
                          <div className="truncate text-sm text-gray-500 dark:text-gray-400">
                            Market cap rank: {result.marketCapRank}
                          </div>
                        )}
                      </div>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            
            <div className="hidden border-t border-gray-200 p-2 text-center text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400 sm:block">
              Press <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">↑</kbd> <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">↓</kbd> to navigate, <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">Enter</kbd> to select, <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">Esc</kbd> to close
            </div>
          </div>
        </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={buttonClasses}
        aria-label="Jump to token (⌘J)"
      >
        <span className="sr-only">Jump</span>
        <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${iconColor}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
        </svg>
      </button>
      {searchDialog}
    </>
  );
};

export default Jump; 